import {
    AccountStatus,
    ModuleSubscription,
    MonthlySummary,
    MonthlySummaryLineItem,
    ProrationResult,
    StatusBadgeConfig,
} from '../types/subscriptionTypes';
import { MODULE_CONFIG } from '../config/subscriptionConfig';

// ─── Trial Helpers ───

/**
 * Calculate days remaining in trial period.
 * Returns 0 if trial has expired.
 */
export function getTrialDaysRemaining(trialEndDate: string | null | undefined): number {
    if (!trialEndDate) return 0;
    const end = new Date(trialEndDate);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
}

/**
 * Check if trial has expired.
 */
export function isTrialExpired(trialEndDate: string | null | undefined): boolean {
    if (!trialEndDate) return true;
    return new Date(trialEndDate) < new Date();
}

// ─── Proration ───

/**
 * Get the next billing date from a given anchor day.
 */
export function getNextBillingDate(billingAnchorDay: number, fromDate: Date = new Date()): Date {
    const year = fromDate.getFullYear();
    const month = fromDate.getMonth();
    const day = fromDate.getDate();

    // If we haven't passed the anchor day this month, billing is this month
    // Otherwise it's next month
    let billingMonth = month;
    let billingYear = year;

    if (day >= billingAnchorDay) {
        billingMonth += 1;
        if (billingMonth > 11) {
            billingMonth = 0;
            billingYear += 1;
        }
    }

    // Clamp anchor day to valid day in target month
    const daysInMonth = new Date(billingYear, billingMonth + 1, 0).getDate();
    const clampedDay = Math.min(billingAnchorDay, daysInMonth);

    return new Date(billingYear, billingMonth, clampedDay);
}

/**
 * Calculate days in the current billing period.
 */
export function getDaysInCurrentPeriod(billingAnchorDay: number, fromDate: Date = new Date()): number {
    const nextBilling = getNextBillingDate(billingAnchorDay, fromDate);
    const prevBilling = new Date(nextBilling);
    prevBilling.setMonth(prevBilling.getMonth() - 1);

    // Clamp prev billing day
    const daysInPrevMonth = new Date(prevBilling.getFullYear(), prevBilling.getMonth() + 1, 0).getDate();
    if (prevBilling.getDate() > daysInPrevMonth) {
        prevBilling.setDate(daysInPrevMonth);
    }

    return Math.round((nextBilling.getTime() - prevBilling.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Calculate proration for a mid-cycle module activation.
 */
export function calculateProration(
    monthlyRate: number,
    billingAnchorDay: number,
    activationDate: Date = new Date()
): ProrationResult {
    const nextBilling = getNextBillingDate(billingAnchorDay, activationDate);
    const totalDays = getDaysInCurrentPeriod(billingAnchorDay, activationDate);
    const daysRemaining = Math.ceil((nextBilling.getTime() - activationDate.getTime()) / (1000 * 60 * 60 * 24));
    const proratedAmount = Math.round(((daysRemaining / totalDays) * monthlyRate) * 100) / 100;

    return {
        proratedAmount,
        daysRemaining,
        totalDaysInPeriod: totalDays,
        fullMonthlyAmount: monthlyRate,
        nextFullChargeDate: nextBilling.toISOString().split('T')[0],
    };
}

/**
 * Calculate per-asset proration.
 */
export function calculatePerAssetProration(
    perUnitRate: number,
    enrolledUnitCount: number,
    billingAnchorDay: number,
    activationDate: Date = new Date()
): ProrationResult {
    const fullMonthly = perUnitRate * enrolledUnitCount;
    return calculateProration(fullMonthly, billingAnchorDay, activationDate);
}

// ─── Monthly Summary ───

/**
 * Build the monthly billing summary from subscription state.
 */
export function buildMonthlySummary(
    coreRate: number,
    moduleSubscriptions: ModuleSubscription[],
    enrolledCounts: Record<string, number>
): MonthlySummary {
    const addOns: MonthlySummaryLineItem[] = [];

    for (const sub of moduleSubscriptions) {
        if (sub.status === 'cancelled') continue;

        const config = MODULE_CONFIG[sub.moduleKey];
        if (!config) continue;

        if (sub.pricingModel === 'per-asset') {
            const count = enrolledCounts[sub.moduleKey] || 0;
            const amount = count * sub.monthlyRate;
            const noun = config.countNoun || 'assets';
            addOns.push({
                label: config.name,
                amount,
                detail: `${count} ${noun} × $${sub.monthlyRate.toFixed(2)}`,
            });
        } else {
            addOns.push({
                label: config.name,
                amount: sub.monthlyRate,
            });
        }
    }

    const total = coreRate + addOns.reduce((sum, item) => sum + item.amount, 0);

    return { coreProgram: coreRate, addOns, total };
}

// ─── Module Access ───

/**
 * Determine if a module is currently accessible.
 * During trial: all modules accessible.
 * During active: only if subscribed (active or pending-cancel before effectiveEndDate).
 * During add-on grace period: accessible.
 */
export function isModuleAccessible(
    accountStatus: AccountStatus | null | undefined,
    moduleSubscription: ModuleSubscription | null | undefined,
    addOnGracePeriodEndDate: string | null | undefined
): boolean {
    // Trial: everything accessible
    if (accountStatus === 'trial') return true;

    // Paused/cancelled/archived: nothing accessible
    if (accountStatus === 'paused' || accountStatus === 'cancelled' || accountStatus === 'archived') return false;

    // Past-due during grace period: still accessible if subscribed
    // Active: check subscription status

    // Add-on grace period (post trial conversion)
    if (addOnGracePeriodEndDate && new Date(addOnGracePeriodEndDate) > new Date()) {
        return true;
    }

    if (!moduleSubscription) return false;

    if (moduleSubscription.status === 'active') return true;

    if (moduleSubscription.status === 'pending-cancel') {
        // Accessible until effectiveEndDate
        if (moduleSubscription.effectiveEndDate) {
            return new Date(moduleSubscription.effectiveEndDate) > new Date();
        }
        return true;
    }

    return false;
}

// ─── Status Badge ───

export function getStatusBadge(accountStatus: AccountStatus | null | undefined): StatusBadgeConfig {
    switch (accountStatus) {
        case 'trial':
            return { label: 'Free Demo', variant: 'info' };
        case 'active':
            return { label: 'Active', variant: 'success' };
        case 'past-due':
            return { label: 'Payment Past Due', variant: 'danger' };
        case 'paused':
            return { label: 'Paused', variant: 'warning' };
        case 'cancelled':
            return { label: 'Cancelled', variant: 'danger' };
        case 'archived':
            return { label: 'Archived', variant: 'neutral' };
        default:
            return { label: 'Unknown', variant: 'neutral' };
    }
}

// ─── Formatting ───

export function formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
}

export function formatDate(isoDate: string | null | undefined): string {
    if (!isoDate) return '—';
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function getDaysUntil(isoDate: string | null | undefined): number {
    if (!isoDate) return 0;
    const target = new Date(isoDate);
    const now = new Date();
    return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}
